import { MapPin, Clock, Eye, MessageSquare } from "lucide-react";
import {
  Badge,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  Button,
} from "@b2b/ui-components";

import { Link } from "react-router-dom";

interface RFQCardProps {
  rfq: {
    id: string;
    title: string;
    company: string;
    location: string;
    postedDate: string;
    expiryDate: string;
    quantity: string;
    quotesCount: number;
    description: string;
    category: string;
    status: "active" | "closed" | "expired";
  };
}

export function RFQCard({ rfq }: RFQCardProps) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "text-white bg-green-600";
      case "closed":
        return "bg-muted text-muted-foreground";
      case "expired":
        return "bg-red-600 text-white";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-card-foreground mb-1 line-clamp-2">
              {rfq.title}
            </h3>
            <p className="text-sm text-muted-foreground">{rfq.company}</p>
          </div>
          <Badge className={getStatusColor(rfq.status)}>
            {rfq.status.toUpperCase()}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pb-3">
        <p className="text-sm text-card-foreground mb-3 line-clamp-2">
          {rfq.description}
        </p>

        <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground mb-3">
          <div className="flex items-center">
            <MapPin className="h-4 w-4 mr-1" />
            {rfq.location}
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            {rfq.postedDate}
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-card-foreground">
            Qty: <span className="font-medium">{rfq.quantity}</span>
          </span>
          <span className="text-muted-foreground">
            {rfq.quotesCount} quotes
          </span>
        </div>
      </CardContent>

      <CardFooter className="pt-3 border-t">
        <div className="flex w-full space-x-2">
          <Button asChild className="flex-1" size="sm" variant="primary">
            <Link to={`/buyer/rfq/${rfq.id}`}>
              <Eye className="h-4 w-4 mr-1" />
              View Details
            </Link>
          </Button>
          <Button asChild variant="outline" size="sm" className="flex-1">
            <Link to={`/buyer/rfq/${rfq.id}`}>
              <MessageSquare className="h-4 w-4 mr-1" />
              View Quotes
            </Link>
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
